import {
  BrandCollection,
  IAppointment,
  IBrand,
  IInvoice,
  IPatient,
  MarketingCampaignScope,
  PatientCollection,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  DocumentReference,
  IXSLXImport,
  asDocRef,
} from '@principle-theorem/shared';
import { Column, Row } from 'exceljs';

export const PATIENT_REPORT_HEADERS: AtLeast<Column, 'key' | 'header'>[] = [
  {
    key: 'patientId',
    header: 'Patient ID',
  },
  {
    key: 'patientName',
    header: 'Patient Name',
  },
  {
    key: 'patientEmail',
    header: 'Patient Email',
  },
  {
    key: 'patientMobile',
    header: 'Patient Mobile Number',
  },
];

export const APPOINTMENT_REPORT_HEADERS: AtLeast<Column, 'key' | 'header'>[] = [
  ...PATIENT_REPORT_HEADERS,
  {
    key: 'appointmentId',
    header: 'Appointment ID',
  },
];

export const INVOICE_REPORT_HEADERS: AtLeast<Column, 'key' | 'header'>[] = [
  ...PATIENT_REPORT_HEADERS,
  {
    key: 'invoiceId',
    header: 'Invoice ID',
  },
  {
    key: 'invoiceReference',
    header: 'Invoice Reference',
  },
];

export interface IPatientReportXSLX {
  patientId: string;
  patientName: string;
  patientEmail: string;
  patientMobile: string;
  patientRef: DocumentReference<IPatient>;
}

export interface IAppointmentReportXSLX extends IPatientReportXSLX {
  appointmentId: string;
  appointmentLabel: string;
  appointmentRef: DocumentReference<IAppointment>;
}

export interface IInvoiceReportXSLX extends IPatientReportXSLX {
  invoiceId: string;
  invoiceReference: string;
  invoiceRef: DocumentReference<IInvoice>;
}

export class XSLXToPatientReport implements IXSLXImport<IPatientReportXSLX> {
  headers = PATIENT_REPORT_HEADERS;

  constructor(private _brandRef: DocumentReference<IBrand>) {}

  translate(row: Row): IPatientReportXSLX {
    const patientId = row.getCell('patientId').value?.toString() ?? '';
    if (!patientId) {
      throw new Error('Patient ID is required for patient report translation');
    }
    const patientRef = asDocRef<IPatient>(
      [this._brandRef.path, BrandCollection.Patients, patientId].join('/')
    );

    return {
      patientId,
      patientName: row.getCell('patientName').value?.toString() ?? '',
      patientEmail: row.getCell('patientEmail').value?.toString() ?? '',
      patientMobile: row.getCell('patientMobile').value?.toString() ?? '',
      patientRef,
    };
  }
}

export class XSLXToAppointmentReport
  implements IXSLXImport<IAppointmentReportXSLX>
{
  private _patientReport: XSLXToPatientReport;
  headers = APPOINTMENT_REPORT_HEADERS;

  constructor(private _brandRef: DocumentReference<IBrand>) {
    this._patientReport = new XSLXToPatientReport(this._brandRef);
  }

  translate(row: Row): IAppointmentReportXSLX {
    const appointmentId = row.getCell('appointmentId').value?.toString() ?? '';
    if (!appointmentId) {
      throw new Error(
        'Appointment ID is required for appointment report translation'
      );
    }

    const patientReport = this._patientReport.translate(row);

    const appointmentRef = asDocRef<IAppointment>(
      [
        patientReport.patientRef.path,
        PatientCollection.Appointments,
        appointmentId,
      ].join('/')
    );

    return {
      ...patientReport,
      appointmentId,
      appointmentRef,
      appointmentLabel: appointmentId,
    };
  }
}

export class XSLXToInvoiceReport implements IXSLXImport<IInvoiceReportXSLX> {
  private _patientReport: XSLXToPatientReport;
  headers = INVOICE_REPORT_HEADERS;

  constructor(private _brandRef: DocumentReference<IBrand>) {
    this._patientReport = new XSLXToPatientReport(this._brandRef);
  }

  translate(row: Row): IInvoiceReportXSLX {
    const invoiceId = row.getCell('invoiceId').value?.toString() ?? '';
    if (!invoiceId) {
      throw new Error('Invoice ID is required for invoice report translation');
    }

    const patientReport = this._patientReport.translate(row);

    const invoiceRef = asDocRef<IInvoice>(
      [
        patientReport.patientRef.path,
        PatientCollection.Appointments,
        invoiceId,
      ].join('/')
    );

    return {
      ...patientReport,
      invoiceId,
      invoiceRef,
      invoiceReference: row.getCell('invoiceReference').value?.toString() ?? '',
    };
  }
}

export function getCampaignScopeToXLXSMap(
  brandRef: DocumentReference<IBrand>
): {
  [key in MarketingCampaignScope]: IXSLXImport<unknown>;
} {
  return {
    [TemplateScope.Patient]: new XSLXToPatientReport(brandRef),
    [TemplateScope.Appointment]: new XSLXToAppointmentReport(brandRef),
    [TemplateScope.Invoice]: new XSLXToInvoiceReport(brandRef),
  };
}
